import {
	configTesterDeployApi as deployApi,
	configTesterValidationApi as validationApi,
} from '../../../api';
import {throwInstanceAlreadyExistsError, isConfigTesterValidationError} from '../../../errors';
import {getScopedAsyncAction} from '../util/async';
import {
	CONFIG_TESTER__ACTIONSCOPE,
	SET_DEPLOY_TYPE__ACTIONTYPE,
	SET_RATER_TYPE__ACTIONTYPE,
	SET_DEPLOY_ERROR_MESSAGE__ACTIONTYPE,
	DEPLOY_CONFIG__ACTIONTYPES,
} from '../../constants/config-tester';

export function setDeployType(deployType: ConfigTester.DeployType): ConfigTester.SetDeployTypeAction {
	return {
		type: SET_DEPLOY_TYPE__ACTIONTYPE,
		deployType,
		scope: CONFIG_TESTER__ACTIONSCOPE,
	};
}

export function setRaterType(raterType: RaterType): ConfigTester.SetRaterTypeAction {
	return {
		type: SET_RATER_TYPE__ACTIONTYPE,
		raterType,
		scope: CONFIG_TESTER__ACTIONSCOPE,
	};
}

export function setDeployErrorMessage(errorMessage: string): ConfigTester.SetDeployErrorMessageAction {
	return {
		type: SET_DEPLOY_ERROR_MESSAGE__ACTIONTYPE,
		errorMessage,
		scope: CONFIG_TESTER__ACTIONSCOPE,
	};
}

function validateTenantName(
		request: ConfigTester.ValidationRequest,
		currentDeployType: ConfigTester.DeployType,
): Promise<ConfigTester.TenantResponse> {
	if (currentDeployType !== 'LAST' && !!request.tenantName) {
		return validationApi.getTenantByName(request)
			.then(tenantResponse => {
				if (currentDeployType === 'TEST') {
					throwInstanceAlreadyExistsError();
				}
				return tenantResponse;
			})
			.catch(error => {
				if (isConfigTesterValidationError(error, currentDeployType)) {
					throw error;
				} else {
					return null;
				}
			});
	} else {
		return Promise.resolve(null);
	}
}

export function deploy(
	validationRequest: ConfigTester.ValidationRequest,
	deployTestRequest: ConfigTester.DeployAssetsRequest,
	externalServiceIntegrationsRequest: ConfigTester.UpdateExternalServiceIntegrationsRequest,
): AsyncAction<ConfigTester.DeployResult> {
	return getScopedAsyncAction(DEPLOY_CONFIG__ACTIONTYPES, CONFIG_TESTER__ACTIONSCOPE, getState => {
		const {deployType, raterType} = getState().configTester.assets;
		if (raterType === 'EXTERNAL_RATER') {
			return deployTestWithExternalServiceIntegrations(
				validationRequest,
				deployTestRequest,
				externalServiceIntegrationsRequest,
				deployType,
				getState().global.user,
			);
		} else {
			// if deploying to previous or existing tenant, we need to check the FFs
			if (deployType === 'LAST' || deployType === 'LOAD') {
				return validateTenantName(validationRequest, deployType)
				.then(() => {
					// get hostname
					return deployApi.getTenantHostname(deployTestRequest.authorizationToken)
					.then(tenantHostname => {
						// tenantHostname will return {username}-configeditor.co.{env}.socotra.com
						// we want to replace `{username}-configeditor` with the provided tenantName
						// so the hostname will be {tenantName}.co.{env}.socotra.com
						const hostname = `${validationRequest.tenantName}.co.${tenantHostname.hostname.split('.co.')[1]}`;
						// get tenantID for hostname
						return deployApi.getTenantIdForTenantName(hostname, deployTestRequest.authorizationToken)
						.then(tenantId => {
							// check FFs of tenant using tenantD
							return deployApi.getTenantFFs(deployTestRequest.authorizationToken, tenantId.tenantId)
							.then(res => {
								// If deploying to existing tenant and repair flag is enabled, deploy with PV
								if (res.configProperties && res.configProperties['property.product.versioning.with.repair.enabled']) {
									const pvDeployReq: ConfigTester.PVDeployAssetRequest = {
										authorizationToken: deployTestRequest.authorizationToken,
										zipFile: deployTestRequest.zipFile,
										tenantName: validationRequest.tenantName,
									};
									return deployWithPV(pvDeployReq, getState().global.user);
								} else {
									return deployTest(deployTestRequest, getState().global.user);
								}
							});
						});
					});
				});
			} else {
				return validateTenantName(validationRequest, deployType)
				.then(() => {
					return deployTest(deployTestRequest, getState().global.user);
				});
			}
		}
	}, 'DONT_FLASH_ERROR');
}

function deployTest(
	deployTestRequest: ConfigTester.DeployAssetsRequest,
	user: UserState,
): Promise<ConfigTester.DeployResult> {
	return deployApi.deployTest(deployTestRequest)
		.then(deployResponse => {
			const lastTenantNameSuffix: string = getTenantNameSuffix(
				user,
				deployResponse);
			return {
				deployResponse,
				lastTenantNameSuffix,
			};
		});
}

function deployWithPV(
	deployTestRequest: ConfigTester.PVDeployAssetRequest,
	user: UserState,
): Promise<ConfigTester.DeployResult> {
	return deployApi.deploy(deployTestRequest)
		.then(deployResponse => {
			const lastTenantNameSuffix: string = getTenantNameSuffix(
				user,
				deployResponse);
			return {
				deployResponse,
				lastTenantNameSuffix,
			};
		});
}

function deployTestWithExternalServiceIntegrations(
	validationRequest: ConfigTester.ValidationRequest,
	deployTestRequest: ConfigTester.DeployAssetsRequest,
	externalServiceIntegrationsRequest: ConfigTester.UpdateExternalServiceIntegrationsRequest,
	deployType: ConfigTester.DeployType,
	user: UserState,
): Promise<ConfigTester.DeployResult> {
	return validateTenantName(validationRequest, deployType)
		.then(() => {
			return deployApi.updateExternalServiceIntegrations(externalServiceIntegrationsRequest)
				.then(updateIntegrationsResponse => {
					const lastTenantNameSuffix: string = getTenantNameSuffix(
						user,
						updateIntegrationsResponse,
					);
					return {
						updateIntegrationsResponse,
						lastTenantNameSuffix,
					};
				}).then(({updateIntegrationsResponse, lastTenantNameSuffix}) => {
					if (updateIntegrationsResponse.success) {
						return deployApi.deployTest(
							getDeployTestRequestOnUpdateIntegrationsSuccess(deployTestRequest, lastTenantNameSuffix),
						).then(deployResponse => {
							return {
								deployResponse,
								lastTenantNameSuffix,
							};
						});
					} else {
						return {
							deployResponse: updateIntegrationsResponse,
							lastTenantNameSuffix,
						};
					}
				});
		});
}

function getDeployTestRequestOnUpdateIntegrationsSuccess(
	deployTestRequest: ConfigTester.DeployAssetsRequest,
	tenantNameSuffix: string,
): ConfigTester.DeployAssetsRequest {
	return {...deployTestRequest, tenantNameSuffix};
}

function getTenantNameSuffix(user: UserState, deployResponse: ConfigTester.DeployResponse) {
	const suffixIndex = user.username.length + 1; // 1 removes the preceding hash
	return deployResponse.tenantName.substring(suffixIndex);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./ts/store/actions/config-tester/assets.ts